<template>
    <div class="wrap">
        <div :id="uniqueId"
             :ref="uniqueId"
             :class="triggerClass"
             @mouseover="startOpenMenu()"
             @mouseout="handleOut()"
             @click="handleClick()"
        >
            <slot></slot>
        </div>
        <div class="hover-wrap"
             v-if="open"
             :class="{'hover-trigger': isHoverTrigger, 'click-trigger': !isHoverTrigger}"
             v-iron-source-click-outside="{
                 handler: 'closeMenu',
                 exclude: [uniqueId]
             }"
             @mouseout="startCloseMenu()"
             @mouseover="cancelTimeouts()">
            <ul>
                <li class="menu-item font-7" v-for="item in menuItems" :key="item.id"
                    @click="actionClicked(item)" :class="{disabled: item.disabled}">
                    <IconBase :height="item.iconData.height"
                              :width="item.iconData.width"
                              :iconName="item.iconData.name"
                              :iconColor="iconColor"
                              class="icon"
                    />
                    {{item.name}}
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import consts from 'services/metaData/consts';
import ironSourceClickOutside from '../../directives/IronSourceClickOutside';
import IconBase from '../IconBase.vue';

const TRIGGER_TYPES = Object.freeze({
    hover: 1,
    click: 2
});

export default {
    name: 'IconMenu',
    components: { IconBase },
    props: {
        uniqueId: String,
        triggerClass: String,
        menuItems: Array, // [{ id, name, iconData: { name, height, width }, payload: {}}]
        disabled: {
            type: Boolean,
            default: false
        },
        trigger: {
            type: String,
            default: 'hover',
            validator(value) {
                return !!TRIGGER_TYPES[value];
            }
        }
    },
    data: () => ({
        open: false,
        openTimeout: null,
        closeTimeout: null
    }),
    computed: {
        iconColor() {
            return consts.COLORS.LIGHT_700;
        },
        isHoverTrigger() {
            return TRIGGER_TYPES[this.trigger] === TRIGGER_TYPES.hover;
        }
    },
    methods: {
        closeMenu() {
            this.open = false;
        },
        openMenu() {
            this.open = true;
        },
        startOpenMenu() {
            if (!this.isHoverTrigger || this.open) {
                return;
            }

            this.openTimeout = setTimeout(() => {
                this.openMenu();
            }, 500);
        },
        startCloseMenu() {
            if (!this.isHoverTrigger) {
                return;
            }

            this.closeTimeout = setTimeout(() => {
                this.closeMenu();
            }, 500);
        },
        cancelTimeouts() {
            if (!this.isHoverTrigger) {
                return;
            }

            if (this.openTimeout) {
                clearTimeout(this.openTimeout);
            }

            if (this.closeTimeout) {
                clearTimeout(this.closeTimeout);
            }
        },
        actionClicked(item) {
            if (item.disabled) {
                return;
            }
            this.$emit('actionClicked', item.id);
            this.cancelTimeouts();
            this.closeMenu();
        },
        handleOut() {
            this.cancelTimeouts();
            this.startCloseMenu();
        },
        handleClick() {
            if (this.isHoverTrigger || this.disabled) {
                return;
            }

            if (!this.open) {
                this.openMenu();
            } else {
                this.closeMenu();
            }
        }
    },
    directives: {
        ironSourceClickOutside
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";

    .wrap {
        position: relative;
    }

    .hover-wrap {
        position: absolute;
        background-color: $white;
        border-radius: 4px;
        box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
        top: 0;
        padding: 4px;
        @include applicationLayer;
        ul {
            list-style: none;
            padding-left: 0;
            padding-right: 0;
            margin-top: 0;
            margin-bottom: 0;
        }
    }

    .menu-item {
        height: 40px;
        line-height: 40px;
        cursor: pointer;
        color: $dark-300;
        padding-left: 15px;
        padding-right: 50px;
        display: grid;
        grid-template-columns: 25px auto;
        white-space: nowrap;

        &:hover {
            background-color: $light-100;
        }

        &.disabled {
            color: $disabledText;
            cursor: default;

            &:hover {
                background-color: $white;
            }
        }
    }

    .icon {
        display: inline-block;
        vertical-align: middle;
        margin-top: 3px;
    }

    .hover-trigger {
        transform: translate(-45px, -26px);
        right: 0px;
    }

    .click-trigger {
        top: 44px;
        left: -139px;
    }
</style>
